import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import moment from "moment";
import {
  getCoinHistoryAPI,
  getUsersByTypeAPI,
} from "../service/AuthService";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

export default function Statement() {
  const toast = useToast();
  const location = useLocation();
  const { id } = useParams();

  const selectedData = location?.state;
  const userData = useSelector((state) => state.account.userData);

  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);

  const [fromDate, setFromDate] = useState(
    moment().subtract(7, "d").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  const [userType] = useState(selectedData?.userType?.toUpperCase());
  const [userName] = useState(
    selectedData?.userName
      ? { value: selectedData?.userName, label: selectedData?.userName }
      : null
  );

  const [reportType, setReportType] = useState("ALL");
  const [filter, setFilter] = useState("ALL");

  /* ================= API CALLS ================= */

  const getAccountStatement = async () => {
    try {
      const startDate = new Date(fromDate).getTime();
      const endDate = new Date(toDate).getTime();

      const { response, code } = await getCoinHistoryAPI(
        id || userName?.value,
        startDate,
        endDate
      );

      if (code === 200) {
        setData(
          (response?.coinHistory || []).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        );
      } else {
        throw response;
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err || "Failed to load statement",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const getUsersByType = async () => {
    const { response, code } = await getUsersByTypeAPI(userType);
    if (code === 200) setUsers(response?.users || []);
  };

  /* ================= FILTER LOGIC ================= */

  const filterObjectsByKeywords = (array, filterType) => {
    if (!filterType || filterType === "ALL") return array;

    // Account statement (Deposit / Withdraw)
    if (filterType === "ACCOUNT") {
      return array.filter(
        (item) => Number(item.credit) > 0 || Number(item.debit) > 0
      );
    }

    // Game report (Profit / Loss / Winner)
    if (Array.isArray(filterType)) {
      return array.filter(
        (item) =>
          item.description &&
          filterType.some((key) =>
            item.description.toLowerCase().includes(key)
          )
      );
    }

    return array;
  };

  /* ================= EFFECTS ================= */

  useEffect(() => {
    getUsersByType();
  }, [userType]);

  useEffect(() => {
    if (id || userName?.value) {
      getAccountStatement();
    }

    if (reportType === "ALL") {
      setFilter("ALL");
    } else if (reportType === "BALANCE_REPORT") {
      setFilter("ACCOUNT");
    } else if (reportType === "GAME_REPORT") {
      setFilter(["profit", "loss", "winner"]);
    }
  }, [reportType, fromDate, toDate]);

  /* ================= JSX ================= */

  return (
    <div
      className="content-wrapper login-work login-all-data p-3"
      style={{ height: "auto", minHeight: "unset" }}
    >
      {/* HEADER */}
      <section className="content-header bredcrumb-data">
        <div className="container-fluid">
          <div className="row w-100 mx-auto">
            <div className="col-sm-6 px-0">
              <h4>Account Statement</h4>
            </div>
            <div className="col-sm-6 px-0">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/app/dashboard">Home</a>
                </li>
                <li className="breadcrumb-item active">Statement</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="user-tablereport">
        <div className="container-fluid px-0">
          <div className="row mx-auto w-100 login-report-table">
            <div className="col-12 px-0">
              <div className="card">

                {/* TABS */}
                <div className="w-full px-sm-4 px-3 py-2">
                  <div className="d-flex gap-2">
                    <button
                      className={`btn ${
                        reportType === "ALL"
                          ? "btn-primary"
                          : "btn-light"
                      }`}
                      onClick={() => setReportType("ALL")}
                    >
                      All
                    </button>

                    <button
                      className={`btn ${
                        reportType === "GAME_REPORT"
                          ? "btn-primary"
                          : "btn-light"
                      }`}
                      onClick={() => setReportType("GAME_REPORT")}
                    >
                      Profit / Loss
                    </button>

                    <button
                      className={`btn ${
                        reportType === "BALANCE_REPORT"
                          ? "btn-primary"
                          : "btn-light"
                      }`}
                      onClick={() => setReportType("BALANCE_REPORT")}
                    >
                      Account Statement
                    </button>
                  </div>
                </div>

                {/* TABLE */}
                <div className="card-body yellow-table-login2">
                  <div className="overflow-x-auto">
                    <table className="table table-bordered table-striped">
                      <thead className="bg-warning">
                        <tr>
                          <th>Statement Id</th>
                          <th>Remark</th>
                          <th>C/D</th>
                          <th>Amount</th>
                          <th>Balance</th>
                          <th>Date & Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterObjectsByKeywords(data, filter)?.map(
                          (item, index) => {
                            const isDebit = Number(item.debit) > 0;
                            const amount = isDebit
                              ? item.debit
                              : item.credit;

                            return (
                              <tr key={index}>
                                <td>{item.id}</td>
                                <td className="minWidthRemark">
                                  {item.description}
                                </td>
                                <td
                                  className={
                                    isDebit ? "text-red" : "text-blue"
                                  }
                                >
                                  {isDebit ? "Withdraw" : "Deposit"}
                                </td>
                                <td
                                  className={
                                    isDebit ? "text-red" : "text-blue"
                                  }
                                >
                                  {amount}
                                </td>
                                <td
                                  className={`font-bold ${
                                    isDebit ? "text-red" : "text-blue"
                                  }`}
                                >
                                  {item.updatedBalance}
                                </td>
                                <td>
                                  {moment(item.createdAt).format(
                                    "DD MMM hh:mm:ss A"
                                  )}
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
